import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtCurrency, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Wallet, Users, Clock, TrendingUp, Loader2, Upload, RefreshCw, Search, Plus,
  FileSpreadsheet, ShieldAlert, CheckCircle2, AlertTriangle, Sparkles, FileText, Gift,
} from "lucide-react";
import { toast } from "sonner";

const BASES = ["hourly", "annual_salary", "casual_hourly", "apprentice", "director_fee", "other"];
const STATUS_TONE = {
  Implemented: "bg-emerald-100 text-emerald-700", Approved: "bg-emerald-100 text-emerald-700",
  Submitted: "bg-blue-100 text-blue-700", Draft: "bg-slate-100 text-slate-700",
  "Under review": "bg-amber-100 text-amber-700", Declined: "bg-rose-100 text-rose-700",
};

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  return <Badge className={`${STATUS_TONE[status] || "bg-slate-100 text-slate-700"} hover:bg-inherit text-[10px]`}>{status}</Badge>;
}

function rateLabel(r) {
  if (r.basis === "annual_salary") return `${fmtCurrency(r.annual_salary)}/yr`;
  return r.current_rate != null || r.new_base != null
    ? `$${Number(r.current_rate ?? r.new_base).toFixed(2)}/hr` : "—";
}

export function RemunerationTab({ canWrite, isExec }) {
  const [sub, setSub] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [register, setRegister] = useState(null);
  const [pending, setPending] = useState([]);
  const [due, setDue] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditQ, setAuditQ] = useState("");
  const [stagingRows, setStagingRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [profile, setProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [benchmark, setBenchmark] = useState([]);
  const [actionTarget, setActionTarget] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [recon, setRecon] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [recentRewards, setRecentRewards] = useState([]);
  const [rewardOpen, setRewardOpen] = useState(false);
  const [budget, setBudget] = useState(null);
  const [lettersOpen, setLettersOpen] = useState(false);
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excluded, setExcluded] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const load = async (which = sub) => {
    setLoading(true);
    try {
      if (which === "overview") setOverview((await api.get("/remuneration/overview")).data);
      if (which === "register") {
        setRegister((await api.get("/remuneration/register")).data);
        setExcluded((await api.get("/remuneration/excluded")).data);
      }
      if (which === "pending") setPending((await api.get("/remuneration/reviews", { params: { status: "pending" } })).data.rows);
      if (which === "due") setDue((await api.get("/remuneration/reports/reviews-due")).data);
      if (which === "audit") setAuditRows((await api.get("/remuneration/audit")).data.rows);
      if (which === "benchmark") setBenchmark((await api.get("/remuneration/reports/benchmark")).data.rows);
      if (which === "payroll") setRecon((await api.get("/remuneration/payroll/reconciliation")).data);
      if (which === "budget") setBudget((await api.get("/remuneration/reports/budget-impact")).data);
      if (which === "rewards") {
        setRewards((await api.get("/remuneration/rewards/summary")).data);
        setRecentRewards((await api.get("/remuneration/rewards", { params: { limit: 25 } })).data.rows);
        if (!register) setRegister((await api.get("/remuneration/register")).data);
      }
      if (which === "import") setStagingRows((await api.get("/remuneration/import/staging")).data.rows);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(sub); /* eslint-disable-next-line */ }, [sub]);

  const openProfile = async (empRef) => {
    try {
      const { data } = await api.get(`/remuneration/employee/${empRef}`);
      setProfile(data); setProfileOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const regRows = useMemo(() => {
    const rows = register?.rows || [];
    if (!search) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [register, search]);

  return (
    <div data-testid="remuneration-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-slate-600 text-xs">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />
          Restricted module — every view and change is audited.
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <Button data-testid="rem-new-review-btn" size="sm" onClick={() => setReviewOpen(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> New Review
            </Button>
          )}
          {canWrite && (
            <Button data-testid="rem-letters-batch-btn" size="sm" variant="outline" onClick={() => setLettersOpen(true)}>
              <FileText className="w-3.5 h-3.5 mr-1" /> Letters ZIP
            </Button>
          )}
          <Button variant="outline" size="sm" data-testid="rem-refresh-btn" onClick={() => load(sub)}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          <TabsTrigger data-testid="rem-sub-overview" value="overview" className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger data-testid="rem-sub-register" value="register" className="text-xs data-[state=active]:bg-white">Register</TabsTrigger>
          <TabsTrigger data-testid="rem-sub-pending" value="pending" className="text-xs data-[state=active]:bg-white">Pending Reviews</TabsTrigger>
          <TabsTrigger data-testid="rem-sub-due" value="due" className="text-xs data-[state=active]:bg-white">Reviews Due</TabsTrigger>
          <TabsTrigger data-testid="rem-sub-benchmark" value="benchmark" className="text-xs data-[state=active]:bg-white">Benchmark</TabsTrigger>
          {canWrite && <TabsTrigger data-testid="rem-sub-payroll" value="payroll" className="text-xs data-[state=active]:bg-white">Payroll Recon</TabsTrigger>}
          <TabsTrigger data-testid="rem-sub-budget" value="budget" className="text-xs data-[state=active]:bg-white">Budget Impact</TabsTrigger>
          <TabsTrigger data-testid="rem-sub-rewards" value="rewards" className="text-xs data-[state=active]:bg-white">Rewards</TabsTrigger>
          {canWrite && <TabsTrigger data-testid="rem-sub-import" value="import" className="text-xs data-[state=active]:bg-white">Import</TabsTrigger>}
          <TabsTrigger data-testid="rem-sub-audit" value="audit" className="text-xs data-[state=active]:bg-white">Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            <Kpi testid="rem-kpi-active" label="Active Employees" value={overview?.active_employees} icon={Users} />
            <Kpi testid="rem-kpi-with" label="With Rem Record" value={overview?.with_record} icon={CheckCircle2} />
            <Kpi testid="rem-kpi-missing" label="Missing Record" value={overview?.missing_record} icon={AlertTriangle} />
            <Kpi testid="rem-kpi-overdue" label="Reviews Overdue" value={overview?.reviews_overdue} icon={Clock} />
            <Kpi testid="rem-kpi-avg" label="Avg Hourly" value={overview?.avg_hourly != null ? `$${overview.avg_hourly}` : "—"} icon={TrendingUp} />
            <Kpi testid="rem-kpi-median" label="Median Hourly" value={overview?.median_hourly != null ? `$${overview.median_hourly}` : "—"} icon={Wallet} />
            <Kpi testid="rem-kpi-pending" label="Pending Reviews" value={overview?.pending_reviews} icon={Clock} />
          </div>
          <div className="text-[11px] text-slate-500 mt-3">
            Estimated total annual base: <span className="font-medium text-slate-700">{fmtCurrency(overview?.total_annual_base)}</span> (hourly rates annualised at std weekly hours × 52 — estimate only)
            {overview?.apprentices_excluded != null && (
              <span className="ml-2" data-testid="rem-apprentice-note">· {overview.apprentices_excluded} apprentices/trainees excluded (GTC-employed — cost rate managed in AroFlo){overview?.manually_excluded ? ` · ${overview.manually_excluded} removed manually` : ""}</span>
            )}
          </div>
          {canWrite && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="rem-ai-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-medium text-slate-800">AI Remuneration Analysis</span>
                  <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]">Advisory only</Badge>
                </div>
                <Button data-testid="rem-ai-run-btn" size="sm" variant="outline" disabled={aiBusy}
                  onClick={async () => {
                    setAiBusy(true);
                    try {
                      const { data } = await api.post(`/remuneration/ai-analysis?refresh=${!!ai}`, null, { timeout: 90000 });
                      setAi(data);
                    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                    finally { setAiBusy(false); }
                  }}>
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  {ai ? "Refresh analysis" : "Run analysis"}
                </Button>
              </div>
              {ai && (
                <div className="mt-3">
                  <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="rem-ai-output">{ai.analysis}</div>
                  <div className="text-[10px] text-slate-400 mt-2">{ai.guardrail} · Generated {(ai.generated_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="register" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
              <Input data-testid="rem-register-search" className="pl-8 h-8 text-xs" placeholder="Search employee…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div className="text-[11px] text-slate-500">{register ? `${register.with_record}/${register.total} have a record` : ""}</div>
            {excluded?.total > 0 && (
              <button data-testid="rem-show-excluded-btn" className="text-[11px] text-slate-500 underline decoration-dotted"
                onClick={() => setShowExcluded((v) => !v)}>
                {showExcluded ? "hide" : "show"} removed ({excluded.total})
              </button>
            )}
            {canWrite && (
              <button data-testid="rem-merge-open-btn" className="text-[11px] text-slate-500 underline decoration-dotted"
                onClick={() => setMergeOpen(true)}>
                merge duplicates
              </button>
            )}
          </div>
          {showExcluded && excluded?.rows?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="rem-excluded-panel">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Removed from remuneration register (history preserved)</div>
              <div className="space-y-1">
                {excluded.rows.map((r) => (
                  <div key={r.emp_ref} className="flex items-center justify-between text-xs bg-white rounded border border-slate-200 px-2.5 py-1.5">
                    <div>
                      <span className="font-medium text-slate-800">{r.name}</span>
                      <span className="text-slate-400 ml-2">{r.position || ""}</span>
                      <span className="text-slate-500 ml-2">{r.reason}</span>
                      <span className="text-slate-400 ml-2">{(r.at || "").slice(0, 10)}</span>
                    </div>
                    {canWrite && (
                      <button data-testid={`rem-restore-emp-${r.emp_ref}`} className="text-[10px] text-emerald-600 hover:underline"
                        onClick={async () => {
                          try { await api.post(`/remuneration/employee/${r.emp_ref}/restore`); toast.success(`${r.name} restored`); load("register"); }
                          catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                        }}>restore</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-register-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">BU</th>
                  <th className="text-left px-3 py-2">Position</th><th className="text-right px-3 py-2">Current Rate</th>
                  <th className="text-right px-3 py-2">Hourly Equiv</th><th className="text-left px-3 py-2">Last Review</th>
                  <th className="text-left px-3 py-2">Next Due</th>
                  <th className="text-right px-3 py-2">Last Incr %</th>
                  {canWrite && <th className="px-2 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {regRows.map((r) => (
                  <tr key={r.emp_ref} data-testid={`rem-register-row-${r.emp_ref}`}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openProfile(r.emp_ref)}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2">{r.bu || "—"}</td>
                    <td className="px-3 py-2">{r.position || "—"}</td>
                    <td className="px-3 py-2 text-right">{r.has_record ? rateLabel(r) : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">No record</Badge>}</td>
                    <td className="px-3 py-2 text-right">{r.hourly_equivalent != null ? `$${Number(r.hourly_equivalent).toFixed(2)}` : "—"}</td>
                    <td className="px-3 py-2">{r.last_review_date || "—"}</td>
                    <td className="px-3 py-2">
                      {r.next_review_due ? (
                        <span className={r.review_flag === "overdue" ? "text-rose-600 font-medium" : r.review_flag === "due_soon" ? "text-amber-600 font-medium" : ""}>
                          {r.next_review_due}
                          {r.review_flag === "overdue" && <Badge className="ml-1.5 bg-rose-100 text-rose-700 hover:bg-rose-100 text-[9px]">overdue</Badge>}
                          {r.review_flag === "due_soon" && <Badge className="ml-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[9px]">due soon</Badge>}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">{r.last_increase_pct != null ? `${r.last_increase_pct}%` : "—"}</td>
                    {canWrite && (
                      <td className="px-2 py-2 text-right">
                        <button data-testid={`rem-remove-emp-${r.emp_ref}`} title="Remove from remuneration register"
                          className="text-[10px] text-slate-300 hover:text-rose-500"
                          onClick={(e) => { e.stopPropagation(); setExcludeTarget({ emp_ref: r.emp_ref, name: r.name }); }}>
                          remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!regRows.length && <tr><td colSpan={canWrite ? 9 : 8} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "No employees"}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-pending-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Current</th><th className="text-right px-3 py-2">Proposed</th>
                  <th className="text-right px-3 py-2">Δ Annual Cost</th><th className="text-left px-3 py-2">Effective</th>
                  <th className="text-left px-3 py-2">Reason / Comment</th>
                  {canWrite && <th className="text-left px-3 py-2">Actions</th>}</tr>
              </thead>
              <tbody>
                {pending.map((r) => (
                  <tr key={r.record_id} className="border-t border-slate-100" data-testid={`rem-pending-row-${r.record_id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.emp_name}</td>
                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 text-right">{r.current_base != null ? `$${r.current_base}` : "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">{rateLabel(r)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.annual_cost_delta != null ? (
                        <span className={r.annual_cost_delta >= 0 ? "text-amber-700" : "text-emerald-700"} title={`+ super impact ${fmtCurrency(r.super_impact)}`}>
                          {fmtCurrency(r.annual_cost_delta)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2">{r.effective_date}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate" title={r.reason}>{r.reason || "—"}</td>
                    {canWrite && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isExec && <button data-testid={`rem-approve-${r.record_id}`} className="text-[10px] text-emerald-600 hover:underline mr-2" onClick={() => setActionTarget({ record: r, action: "approve" })}>Approve</button>}
                        {isExec && <button data-testid={`rem-decline-${r.record_id}`} className="text-[10px] text-rose-600 hover:underline mr-2" onClick={() => setActionTarget({ record: r, action: "decline" })}>Decline</button>}
                        {["Draft", "Further information required"].includes(r.status) && <button data-testid={`rem-submit-${r.record_id}`} className="text-[10px] text-blue-600 hover:underline mr-2" onClick={() => setActionTarget({ record: r, action: "submit" })}>Submit</button>}
                        {["Submitted", "Under review"].includes(r.status) && <button data-testid={`rem-info-${r.record_id}`} className="text-[10px] text-amber-600 hover:underline" onClick={() => setActionTarget({ record: r, action: "request_info" })}>Info</button>}
                      </td>
                    )}
                  </tr>
                ))}
                {!pending.length && <tr><td colSpan={canWrite ? 8 : 7} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "No pending reviews"}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="benchmark" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-benchmark-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">Position</th><th className="text-right px-3 py-2">People</th>
                  <th className="text-right px-3 py-2">Min</th><th className="text-right px-3 py-2">Median</th>
                  <th className="text-right px-3 py-2">Avg</th><th className="text-right px-3 py-2">Max</th>
                  <th className="text-right px-3 py-2">Spread</th></tr>
              </thead>
              <tbody>
                {benchmark.map((r) => (
                  <tr key={r.position} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{r.position}</td>
                    <td className="px-3 py-2 text-right">{r.count}</td>
                    <td className="px-3 py-2 text-right">${r.min}</td>
                    <td className="px-3 py-2 text-right font-medium">${r.median}</td>
                    <td className="px-3 py-2 text-right">${r.avg}</td>
                    <td className="px-3 py-2 text-right">${r.max}</td>
                    <td className="px-3 py-2 text-right">{r.spread_pct != null ? `${r.spread_pct}%` : "—"}</td>
                  </tr>
                ))}
                {!benchmark.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "No data — commit remuneration records first"}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">Hourly-equivalent rates for active employees, grouped by position (salaries converted at std weekly hours × 52).</div>
        </TabsContent>

        <TabsContent value="due" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-due-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">BU</th>
                  <th className="text-left px-3 py-2">Position</th><th className="text-right px-3 py-2">Current</th>
                  <th className="text-left px-3 py-2">Last Review</th><th className="text-left px-3 py-2">Next Due</th>
                  <th className="text-right px-3 py-2">Months Since</th><th className="text-left px-3 py-2">Issue</th></tr>
              </thead>
              <tbody>
                {(due?.rows || []).map((r) => (
                  <tr key={r.emp_ref} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openProfile(r.emp_ref)}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-2">{r.bu || "—"}</td>
                    <td className="px-3 py-2">{r.position || "—"}</td>
                    <td className="px-3 py-2 text-right">{r.current_rate != null ? `$${r.current_rate}` : "—"}</td>
                    <td className="px-3 py-2">{r.last_review_date || "—"}</td>
                    <td className="px-3 py-2">{r.next_review_due || "—"}</td>
                    <td className="px-3 py-2 text-right">{r.months_since_review ?? "—"}</td>
                    <td className="px-3 py-2"><Badge className={`${r.issue.startsWith("No") ? "bg-amber-100 text-amber-700" : r.issue.includes("30 days") ? "bg-orange-100 text-orange-700" : "bg-rose-100 text-rose-700"} hover:bg-inherit text-[10px]`}>{r.issue}</Badge></td>
                  </tr>
                ))}
                {!due?.rows?.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "Nothing due"}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {canWrite && (
          <TabsContent value="import" className="mt-4">
            <ImportPanel rows={stagingRows} reload={() => load("import")} />
          </TabsContent>
        )}

        {canWrite && (
          <TabsContent value="payroll" className="mt-4">
            <PayrollReconPanel recon={recon} reload={() => load("payroll")} />
          </TabsContent>
        )}

        <TabsContent value="budget" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-budget-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">BU</th><th className="text-right px-3 py-2">Headcount</th>
                  <th className="text-right px-3 py-2">Current Annual Base</th>
                  <th className="text-right px-3 py-2">Approved (future)</th>
                  <th className="text-right px-3 py-2">Pending</th>
                  <th className="text-right px-3 py-2">Super Impact</th>
                  <th className="text-right px-3 py-2">Uplift %</th>
                  <th className="text-right px-3 py-2">Forecast Base</th></tr>
              </thead>
              <tbody>
                {(budget?.rows || []).map((r) => (
                  <tr key={r.bu} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{r.bu}</td>
                    <td className="px-3 py-2 text-right">{r.headcount}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.current_annual_base)}</td>
                    <td className="px-3 py-2 text-right">{r.approved_future_delta ? <span className="text-emerald-700">+{fmtCurrency(r.approved_future_delta)} ({r.approved_future_count})</span> : "—"}</td>
                    <td className="px-3 py-2 text-right">{r.pending_delta ? <span className="text-amber-700">+{fmtCurrency(r.pending_delta)} ({r.pending_count})</span> : "—"}</td>
                    <td className="px-3 py-2 text-right">{r.super_impact ? fmtCurrency(r.super_impact) : "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">{r.uplift_pct != null ? `${r.uplift_pct}%` : "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCurrency(r.forecast_annual_base)}</td>
                  </tr>
                ))}
                {budget?.totals && (
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td className="px-3 py-2">Total</td><td />
                    <td className="px-3 py-2 text-right">{fmtCurrency(budget.totals.current_annual_base)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">+{fmtCurrency(budget.totals.approved_future_delta)}</td>
                    <td className="px-3 py-2 text-right text-amber-700">+{fmtCurrency(budget.totals.pending_delta)}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(budget.totals.super_impact)}</td><td />
                    <td className="px-3 py-2 text-right">{fmtCurrency(budget.totals.forecast_annual_base)}</td>
                  </tr>
                )}
                {!budget?.rows?.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "No data"}</td></tr>}
              </tbody>
            </table>
          </div>
          {budget?.note && <div className="text-[11px] text-slate-500 mt-2">{budget.note}</div>}
        </TabsContent>

        <TabsContent value="rewards" className="mt-4">
          <RewardsPanel rewards={rewards} recent={recentRewards} canWrite={canWrite}
            onIssue={() => setRewardOpen(true)} reload={() => load("rewards")} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Input placeholder="Filter by employee, user or action…" value={auditQ}
                   data-testid="rem-audit-search"
                   onChange={(e) => setAuditQ(e.target.value)}
                   onKeyDown={async (e) => {
                     if (e.key === "Enter") setAuditRows((await api.get(`/remuneration/audit?q=${encodeURIComponent(auditQ)}`)).data.rows);
                   }}
                   className="h-8 text-xs max-w-xs" />
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="rem-audit-search-btn"
                    onClick={async () => setAuditRows((await api.get(`/remuneration/audit?q=${encodeURIComponent(auditQ)}`)).data.rows)}>
              Filter
            </Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="rem-audit-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Action</th><th className="text-left px-3 py-2">Detail</th></tr>
              </thead>
              <tbody>
                {auditRows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 whitespace-nowrap">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2">{r.user}</td>
                    <td className="px-3 py-2"><Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{r.action}</Badge></td>
                    <td className="px-3 py-2 max-w-[400px] truncate" title={r.detail}>{r.detail}</td>
                  </tr>
                ))}
                {!auditRows.length && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">{loading ? "Loading…" : "No audit entries"}</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} profile={profile} canWrite={canWrite}
        refresh={() => profile && openProfile(profile.emp_ref)} />
      {canWrite && (
        <NewReviewDialog open={reviewOpen} onOpenChange={setReviewOpen}
          registerRows={register?.rows || []}
          onCreated={() => { setReviewOpen(false); load(sub); }} />
      )}
      <WorkflowActionDialog target={actionTarget} onClose={() => setActionTarget(null)}
        onDone={() => { setActionTarget(null); load("pending"); }} />
      {canWrite && (
        <IssueRewardDialog open={rewardOpen} onOpenChange={setRewardOpen}
          registerRows={register?.rows || []}
          onCreated={() => { setRewardOpen(false); load("rewards"); }} />
      )}
      {canWrite && <LettersBatchDialog open={lettersOpen} onOpenChange={setLettersOpen} />}
      {canWrite && (
        <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen}
          onMerged={() => { setMergeOpen(false); load("register"); }} />
      )}
      {canWrite && (
        <ExcludeDialog target={excludeTarget} onClose={() => setExcludeTarget(null)}
          onDone={() => { setExcludeTarget(null); load("register"); }} />
      )}
    </div>
  );
}

function MergeDialog({ open, onOpenChange, onMerged }) {
  const [suggestions, setSuggestions] = useState([]);
  const [cands, setCands] = useState([]);
  const [primary, setPrimary] = useState("");
  const [dup, setDup] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/remuneration/merge/suggestions").then(({ data }) => setSuggestions(data.pairs || [])).catch(() => {});
    api.get("/remuneration/match-candidates").then(({ data }) => setCands(data.rows || [])).catch(() => {});
  }, [open]);

  const doMerge = async (p, d, names) => {
    if (!p || !d) { toast.error("Pick both the employee to KEEP and the duplicate."); return; }
    if (!window.confirm(`Merge ${names.dup} into ${names.prim}? All pay records, rewards and allowances move to ${names.prim}; ${names.dup} is deactivated (nothing is deleted).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/remuneration/merge", { primary_ref: p, duplicate_ref: d });
      const movedTotal = Object.values(data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`${data.duplicate} merged into ${data.primary} — ${movedTotal} records moved${data.backfilled_fields?.length ? `, back-filled: ${data.backfilled_fields.join(", ")}` : ""}`);
      onMerged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const nameOf = (ref) => cands.find((c) => c.emp_ref === ref)?.name || "?";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="rem-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge duplicate employees</DialogTitle>
          <DialogDescription>Moves all remuneration data onto the employee you keep; the duplicate is deactivated, never deleted.</DialogDescription>
        </DialogHeader>
        {suggestions.length > 0 && (
          <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="rem-merge-suggestions">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Suggested duplicates</div>
            {suggestions.map((s, i) => (
              <div key={i} className="flex items-center justify-between rounded border border-slate-200 px-2.5 py-1.5 text-xs">
                <div>
                  <span className="font-medium text-slate-800">{s.primary.name}</span>
                  <span className="text-slate-400 mx-1">← keep, merge in →</span>
                  <span className="font-medium text-slate-800">{s.duplicate.name}</span>
                  <span className="text-slate-400 ml-2">({s.reason}; {s.duplicate.rem_records} records to move)</span>
                </div>
                <button data-testid={`rem-merge-suggested-${i}`} disabled={busy} className="text-[10px] text-blue-600 hover:underline"
                  onClick={() => doMerge(s.primary.emp_ref, s.duplicate.emp_ref, { prim: s.primary.name, dup: s.duplicate.name })}>
                  merge
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Keep (primary)</Label>
            <Select value={primary} onValueChange={setPrimary}>
              <SelectTrigger data-testid="rem-merge-primary-select" className="h-8 text-xs"><SelectValue placeholder="Employee to keep…" /></SelectTrigger>
              <SelectContent className="max-h-56">
                {cands.map((c) => <SelectItem key={c.emp_ref} value={c.emp_ref} className="text-xs">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Duplicate (merged away)</Label>
            <Select value={dup} onValueChange={setDup}>
              <SelectTrigger data-testid="rem-merge-duplicate-select" className="h-8 text-xs"><SelectValue placeholder="Duplicate…" /></SelectTrigger>
              <SelectContent className="max-h-56">
                {cands.filter((c) => c.emp_ref !== primary).map((c) => <SelectItem key={c.emp_ref} value={c.emp_ref} className="text-xs">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rem-merge-confirm-btn" size="sm" disabled={busy || !primary || !dup}
            onClick={() => doMerge(primary, dup, { prim: nameOf(primary), dup: nameOf(dup) })}>
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Merge duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExcludeDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/remuneration/employee/${target.emp_ref}/exclude`, { reason: reason || null });
      toast.success(`${target.name} removed from the remuneration register`);
      setReason(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="rem-exclude-dialog">
        <DialogHeader>
          <DialogTitle>Remove {target?.name}?</DialogTitle>
          <DialogDescription>Removes them from the remuneration register and reviews-due reports only — their HR record and pay history are kept and they can be restored anytime.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="rem-exclude-reason-input" className="text-xs" rows={2}
          placeholder="Reason (e.g. GTC trainee, contractor, left the business)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter>
          <Button data-testid="rem-exclude-confirm-btn" size="sm" variant="destructive" onClick={run} disabled={busy}>
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Remove from register
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayrollReconPanel({ recon, reload }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/remuneration/payroll/reconcile", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(`Reconciled ${data.summary.total} payroll rows — ${data.summary.match} match, ${data.summary.variance} variances`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const rows = (recon?.rows || []).filter((r) => filter === "all" || r.status === filter);
  const STATUS_TONE_R = { match: "bg-emerald-100 text-emerald-700", variance: "bg-rose-100 text-rose-700", no_register_record: "bg-amber-100 text-amber-700", no_employee_match: "bg-slate-100 text-slate-600", no_payroll_value: "bg-slate-100 text-slate-500" };
  return (
    <div className="space-y-3" data-testid="rem-payroll-panel">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <FileSpreadsheet className="w-4 h-4 text-slate-400" />
        <div className="text-xs text-slate-600 flex-1 min-w-[220px]">
          Upload a MYOB payroll export (.xlsx or .csv with last name / first name / salary-rate columns). Variances are <b>reported only</b> — the register is never overwritten.
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="rem-payroll-file-input" />
        <Button data-testid="rem-payroll-upload-btn" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Upload payroll export
        </Button>
      </div>
      {recon?.summary && (
        <>
          <div className="flex gap-1 flex-wrap">
            {["all", "variance", "no_register_record", "no_employee_match", "match"].map((f) => (
              <button key={f} data-testid={`rem-payroll-filter-${f}`} onClick={() => setFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[10px] border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
                {f.replace(/_/g, " ")} ({f === "all" ? recon.summary.total : recon.summary[f] ?? recon.rows.filter((r) => r.status === f).length})
              </button>
            ))}
            <span className="ml-auto text-[10px] text-slate-400 self-center">
              {recon.filename} · {(recon.run_at || "").slice(0, 16).replace("T", " ")} by {recon.run_by}
            </span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="rem-payroll-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr><th className="text-left px-3 py-2">Payroll Name</th><th className="text-left px-3 py-2">Matched</th>
                  <th className="text-right px-3 py-2">Payroll</th><th className="text-right px-3 py-2">Register</th>
                  <th className="text-right px-3 py-2">Variance</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${r.status === "variance" ? "bg-rose-50/40" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-slate-800">{r.name}</td>
                    <td className="px-3 py-1.5">{r.matched_name || "—"}</td>
                    <td className="px-3 py-1.5 text-right">{r.payroll_value != null ? (r.payroll_value >= 5000 ? fmtCurrency(r.payroll_value) : `$${r.payroll_value}`) : "—"}</td>
                    <td className="px-3 py-1.5 text-right">{r.register_value != null ? (r.register_value >= 5000 ? fmtCurrency(r.register_value) : `$${r.register_value}`) : "—"}</td>
                    <td className={`px-3 py-1.5 text-right font-medium ${r.variance ? "text-rose-600" : "text-slate-500"}`}>{r.variance != null ? (r.variance > 0 ? `+${r.variance}` : r.variance) : "—"}</td>
                    <td className="px-3 py-1.5"><Badge className={`${STATUS_TONE_R[r.status]} hover:bg-inherit text-[10px]`}>{r.status?.replace(/_/g, " ")}</Badge></td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No rows in this filter</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!recon?.summary && <div className="text-xs text-slate-400 text-center py-6">No reconciliation run yet — upload a payroll export to compare against the register.</div>}
    </div>
  );
}

function RewardsPanel({ rewards, recent, canWrite, onIssue, reload }) {
  const [view, setView] = useState("fairness");
  const k = rewards?.kpis;
  const del = async (id) => {
    try { await api.delete(`/remuneration/rewards/${id}`); toast.success("Reward entry deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="rem-rewards-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-600 flex items-center gap-2">
          <Gift className="w-4 h-4 text-pink-500" />
          Appreciation gift cards — tracked so recognition is spread fairly across the team.
        </div>
        {canWrite && (
          <Button data-testid="rem-issue-reward-btn" size="sm" onClick={onIssue}>
            <Gift className="w-3.5 h-3.5 mr-1" /> Issue gift card
          </Button>
        )}
      </div>
      {k && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi testid="rew-kpi-fy" label="Issued This FY" value={fmtCurrency(k.total_issued_fy)} sub={`${k.cards_fy} cards`} icon={Gift} />
          <Kpi testid="rew-kpi-all" label="All Time" value={fmtCurrency(k.total_issued_all_time)} icon={Wallet} />
          <Kpi testid="rew-kpi-rewarded" label="Employees Rewarded" value={`${k.employees_rewarded}/${k.employees_total}`} icon={Users} />
          <Kpi testid="rew-kpi-never" label="Never Rewarded" value={k.never_rewarded} icon={AlertTriangle} />
          <Kpi testid="rew-kpi-avg" label="Avg per Rewarded" value={fmtCurrency(k.avg_per_rewarded)} icon={TrendingUp} />
        </div>
      )}
      <div className="flex gap-1">
        {["fairness", "recent"].map((v) => (
          <button key={v} data-testid={`rem-rewards-view-${v}`} onClick={() => setView(v)}
            className={`px-2.5 py-1 rounded-full text-[10px] border ${view === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
            {v === "fairness" ? "Fair distribution" : `Recent issues (${recent.length})`}
          </button>
        ))}
      </div>
      {view === "fairness" ? (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="rem-rewards-table">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
              <tr><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">BU</th>
                <th className="text-left px-3 py-2">Position</th><th className="text-right px-3 py-2">Cards</th>
                <th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2">This FY</th>
                <th className="text-left px-3 py-2">Last Issued</th><th className="text-left px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {(rewards?.rows || []).map((r) => (
                <tr key={r.emp_ref} className={`border-t border-slate-100 ${r.never_rewarded ? "bg-amber-50/30" : ""}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.name}</td>
                  <td className="px-3 py-1.5">{r.bu || "—"}</td>
                  <td className="px-3 py-1.5">{r.position || "—"}</td>
                  <td className="px-3 py-1.5 text-right">{r.count || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{r.total ? fmtCurrency(r.total) : "—"}</td>
                  <td className="px-3 py-1.5 text-right">{r.fy_total ? fmtCurrency(r.fy_total) : "—"}</td>
                  <td className="px-3 py-1.5">{r.last_issued || "—"}{r.months_since != null && r.months_since > 12 && <span className="text-amber-600 ml-1">({r.months_since}m ago)</span>}</td>
                  <td className="px-3 py-1.5">{r.never_rewarded && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">never rewarded</Badge>}</td>
                </tr>
              ))}
              {!rewards?.rows?.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No employees</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="rem-rewards-recent-table">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Employee</th>
                <th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2">Card</th>
                <th className="text-left px-3 py-2">Reason</th><th className="text-left px-3 py-2">Issued By</th>
                <th className="text-left px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.reward_id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.issued_date}</td>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.emp_name}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{fmtCurrency(r.amount)}</td>
                  <td className="px-3 py-1.5">{r.card_type || "—"}</td>
                  <td className="px-3 py-1.5 max-w-[280px] truncate" title={r.reason}>{r.reason || "—"}</td>
                  <td className="px-3 py-1.5">{r.issued_by}</td>
                  <td className="px-3 py-1.5">
                    {canWrite && <button data-testid={`rem-reward-delete-${r.reward_id}`} className="text-[10px] text-slate-300 hover:text-rose-500" onClick={() => del(r.reward_id)}>delete</button>}
                  </td>
                </tr>
              ))}
              {!recent.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No gift cards issued yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IssueRewardDialog({ open, onOpenChange, registerRows, onCreated }) {
  const [form, setForm] = useState({ emp_ref: "", amount: "", card_type: "", reason: "", issued_date: new Date().toISOString().slice(0, 10) });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.emp_ref || !form.amount) { toast.error("Employee and amount are required."); return; }
    setSaving(true);
    try {
      await api.post("/remuneration/rewards", { emp_ref: form.emp_ref, amount: Number(form.amount), issued_date: form.issued_date, card_type: form.card_type || null, reason: form.reason || null });
      toast.success("Gift card recorded");
      setForm({ emp_ref: "", amount: "", card_type: "", reason: "", issued_date: new Date().toISOString().slice(0, 10) });
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rem-reward-dialog">
        <DialogHeader>
          <DialogTitle>Issue appreciation gift card</DialogTitle>
          <DialogDescription>Recorded against the employee so recognition stays fairly distributed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <Label className="text-xs">Employee</Label>
            <Select value={form.emp_ref} onValueChange={(v) => set("emp_ref", v)}>
              <SelectTrigger data-testid="rem-reward-employee-select" className="h-8 text-xs"><SelectValue placeholder="Select employee…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {registerRows.map((r) => <SelectItem key={r.emp_ref} value={r.emp_ref} className="text-xs">{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input data-testid="rem-reward-amount-input" type="number" className="h-8 text-xs" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Date issued</Label>
              <Input data-testid="rem-reward-date-input" type="date" className="h-8 text-xs" value={form.issued_date} onChange={(e) => set("issued_date", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Card type</Label>
              <Input data-testid="rem-reward-card-input" className="h-8 text-xs" placeholder="e.g. Visa, Bunnings" value={form.card_type} onChange={(e) => set("card_type", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Textarea data-testid="rem-reward-reason-input" className="text-xs" rows={2} placeholder="e.g. outstanding client feedback on UoN shutdown" value={form.reason} onChange={(e) => set("reason", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rem-reward-submit-btn" size="sm" onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Record gift card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LettersBatchDialog({ open, onOpenChange }) {
  const fy = new Date().getMonth() >= 6 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const [from, setFrom] = useState(`${fy}-07-01`);
  const [to, setTo] = useState(`${fy + 1}-06-30`);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/remuneration/letters-batch?date_from=${from}&date_to=${to}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Increase_Letters_${from}_to_${to}.zip`; a.click();
      URL.revokeObjectURL(url);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No approved reviews with an effective date in that range" : "Could not generate letters");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="rem-letters-dialog">
        <DialogHeader>
          <DialogTitle>Increase letters — batch export</DialogTitle>
          <DialogDescription>One PDF per approved review with an effective date in the range (workbook history imports excluded), zipped.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">From</Label><Input data-testid="rem-letters-from" type="date" className="h-8 text-xs" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label className="text-xs">To</Label><Input data-testid="rem-letters-to" type="date" className="h-8 text-xs" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="rem-letters-download-btn" size="sm" onClick={run} disabled={busy}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}Download ZIP
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkflowActionDialog({ target, onClose, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const LABELS = { approve: "Approve", decline: "Decline", submit: "Submit for approval", request_info: "Request further information" };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/remuneration/reviews/${target.record.record_id}/action`, { action: target.action, comment: comment || null });
      toast.success(`${LABELS[target.action]} — status: ${data.status}${data.awaiting_effective_date ? " (awaiting effective date)" : ""}`);
      setComment(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="rem-action-dialog">
        <DialogHeader>
          <DialogTitle>{target ? LABELS[target.action] : ""}</DialogTitle>
          <DialogDescription>
            {target ? `${target.record.emp_name} — ${target.record.basis === "annual_salary" ? fmtCurrency(target.record.annual_salary) + "/yr" : "$" + target.record.new_base + "/hr"} effective ${target.record.effective_date}` : ""}
          </DialogDescription>
        </DialogHeader>
        <Textarea data-testid="rem-action-comment-input" className="text-xs" rows={2} placeholder="Comment (optional, kept on the record)" value={comment} onChange={(e) => setComment(e.target.value)} />
        <DialogFooter>
          <Button data-testid="rem-action-confirm-btn" size="sm" onClick={run} disabled={busy}
            variant={target?.action === "decline" ? "destructive" : "default"}>
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{target ? LABELS[target.action] : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllowanceSection({ profile, canWrite, refresh }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: "", amount: "", frequency: "weekly", start_date: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.type || !form.amount || !form.start_date) { toast.error("Type, amount and start date required."); return; }
    try {
      await api.post("/remuneration/allowances", { emp_ref: profile.emp_ref, type: form.type, amount: Number(form.amount), frequency: form.frequency, start_date: form.start_date });
      toast.success("Allowance added"); setAdding(false); setForm({ type: "", amount: "", frequency: "weekly", start_date: "" }); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const end = async (id) => {
    try {
      await api.patch(`/remuneration/allowances/${id}/end`, { end_date: new Date().toISOString().slice(0, 10) });
      toast.success("Allowance ended"); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <div className="mt-4" data-testid="rem-allowances-section">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Allowances (separate from base rate)</div>
        {canWrite && <button data-testid="rem-add-allowance-btn" className="text-[10px] text-blue-600 hover:underline" onClick={() => setAdding((v) => !v)}>{adding ? "cancel" : "+ add"}</button>}
      </div>
      {adding && (
        <div className="grid grid-cols-2 gap-2 mt-2 rounded-lg border border-slate-200 p-2">
          <Input data-testid="rem-allowance-type-input" className="h-7 text-xs" placeholder="Type (e.g. fuel card)" value={form.type} onChange={(e) => set("type", e.target.value)} />
          <Input data-testid="rem-allowance-amount-input" className="h-7 text-xs" type="number" placeholder="Amount $" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
          <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["weekly", "fortnightly", "monthly", "annual", "per_shift"].map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="rem-allowance-start-input" className="h-7 text-xs" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
          <Button data-testid="rem-allowance-save-btn" size="sm" className="col-span-2 h-7 text-xs" onClick={submit}>Save allowance</Button>
        </div>
      )}
      <div className="space-y-1.5 mt-2">
        {(profile?.allowances || []).map((a) => (
          <div key={a.allowance_id} className="flex items-center justify-between rounded border border-slate-200 px-2.5 py-1.5 text-xs" data-testid="rem-allowance-item">
            <div>
              <span className="font-medium text-slate-800">{a.type}</span>
              <span className="text-slate-500 ml-2">${a.amount}/{a.frequency}</span>
              <span className="text-slate-400 ml-2">{a.start_date} → {a.end_date || "open"}</span>
            </div>
            {canWrite && !a.end_date && <button className="text-[10px] text-rose-500 hover:underline" onClick={() => end(a.allowance_id)}>end</button>}
          </div>
        ))}
        {!profile?.allowances?.length && !adding && <div className="text-[11px] text-slate-400">None recorded.</div>}
      </div>
    </div>
  );
}

function ProfileSheet({ open, onOpenChange, profile, canWrite, refresh }) {
  const [editDate, setEditDate] = useState(null);
  const saveDate = async (recordId, value, current) => {
    setEditDate(null);
    if (!value || value === current) return;
    try {
      await api.patch(`/remuneration/records/${recordId}/effective-date`, { effective_date: value });
      toast.success(`Effective date updated to ${value}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="rem-profile-sheet">
        <SheetHeader>
          <SheetTitle>{profile?.name || "Employee"}</SheetTitle>
          <SheetDescription>{profile?.position || ""} — remuneration timeline (immutable history)</SheetDescription>
        </SheetHeader>
        {profile?.current && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">Current rate</div>
            <div className="text-xl font-semibold text-slate-900 mt-1">{rateLabel({ ...profile.current, current_rate: profile.current.new_base })}</div>
            <div className="text-slate-500 mt-0.5">Effective {profile.current.effective_date} ({profile.current.effective_date_confidence || "—"})</div>
          </div>
        )}
        {profile?.performance && (
          <div className="mt-4 rounded-lg border border-slate-200 p-3 text-xs" data-testid="rem-performance-block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Performance context ({profile.performance.window})</div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div><div className="text-base font-semibold text-slate-900">{profile.performance.productive_pct}%</div><div className="text-[10px] text-slate-400">productive</div></div>
              <div><div className="text-base font-semibold text-slate-900">{profile.performance.productive_hours}h</div><div className="text-[10px] text-slate-400">of {profile.performance.total_hours}h</div></div>
              <div><div className="text-base font-semibold text-slate-900">{profile.performance.field_days}</div><div className="text-[10px] text-slate-400">field days</div></div>
            </div>
            {profile.performance.timesheet_on_time_pct != null && (
              <div className="text-[10px] text-slate-400 mt-1.5 text-center">Timesheets on time: {profile.performance.timesheet_on_time_pct}%</div>
            )}
          </div>
        )}
        <AllowanceSection profile={profile} canWrite={canWrite} refresh={refresh} />
        <div className="mt-4 space-y-2">
          {(profile?.timeline || []).slice().reverse().map((r) => (
            <div key={r.record_id} className="rounded-lg border border-slate-200 p-3 text-xs" data-testid="rem-timeline-item">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">{rateLabel({ ...r, current_rate: r.new_base })}</span>
                <span className="flex items-center gap-1">
                  {r.status === "Approved" && r.effective_date > new Date().toISOString().slice(0, 10) &&
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">awaiting effective date</Badge>}
                  <StatusBadge status={r.status} />
                </span>
              </div>
              <div className="text-slate-500 mt-1">
                Effective{" "}
                {editDate === r.record_id ? (
                  <Input type="date" autoFocus defaultValue={r.effective_date}
                    className="inline-block h-6 w-32 text-[10px] align-middle"
                    data-testid="rem-edit-date-input"
                    onBlur={(e) => saveDate(r.record_id, e.target.value, r.effective_date)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveDate(r.record_id, e.target.value, r.effective_date); }} />
                ) : canWrite ? (
                  <button data-testid={`rem-edit-date-btn-${r.record_id}`}
                    className="underline decoration-dotted hover:text-slate-800"
                    title="Click to correct the effective date"
                    onClick={() => setEditDate(r.record_id)}>
                    {r.effective_date}
                    {r.effective_date_confidence === "Year only" && <span className="text-amber-500 ml-0.5" title="Year only — click to confirm exact date">≈</span>}
                  </button>
                ) : (
                  r.effective_date
                )}{" "}
                · {r.review_type || "—"}
                {r.increase_pct != null && <span className="ml-2 text-emerald-600">+{r.increase_pct}%</span>}
              </div>
              {r.reason && <div className="text-slate-600 mt-1">{r.reason}</div>}
              {r.approver && <div className="text-[10px] text-slate-400 mt-1">Approved by {r.approver}</div>}
              {(r.created_by || r.workflow_history?.length > 0) && (
                <details className="mt-1.5" data-testid={`rem-history-${r.record_id}`}>
                  <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600 select-none">
                    Change history{r.workflow_history?.length > 0 ? ` (${r.workflow_history.length})` : ""}
                  </summary>
                  <div className="mt-1 pl-2 border-l-2 border-slate-100 space-y-0.5">
                    {r.created_by && (
                      <div className="text-[10px] text-slate-500">
                        <span className="text-slate-400">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>{" "}
                        created by <span className="font-medium">{r.created_by}</span>
                      </div>
                    )}
                    {(r.workflow_history || []).map((h, hi) => (
                      <div key={hi} className="text-[10px] text-slate-500">
                        <span className="text-slate-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>{" "}
                        <span className={`font-medium ${h.action === "approve" ? "text-emerald-600" : h.action === "decline" ? "text-rose-600" : ""}`}>{h.action.replace("_", " ")}</span>
                        {" "}by <span className="font-medium">{h.by}</span>
                        {h.from && h.to && <span className="text-slate-400"> ({h.from} → {h.to})</span>}
                        {h.comment && <span className="text-slate-400 italic"> — "{h.comment}"</span>}
                      </div>
                    ))}
                    {(r.edits || []).map((ed, ei) => (
                      <div key={`e${ei}`} className="text-[10px] text-slate-500">
                        <span className="text-slate-400">{(ed.at || "").slice(0, 16).replace("T", " ")}</span>{" "}
                        <span className="font-medium text-amber-600">date corrected</span> by <span className="font-medium">{ed.by}</span>
                        <span className="text-slate-400"> ({ed.from} → {ed.to})</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {canWrite && ["Approved", "Implemented"].includes(r.status) && (
                <button data-testid={`rem-letter-btn-${r.record_id}`}
                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      const res = await api.get(`/remuneration/letters/${r.record_id}`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `Remuneration_Letter_${(profile?.name || "employee").replace(/ /g, "_")}_${r.effective_date}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { toast.error("Could not generate letter"); }
                  }}>
                  <FileText className="w-3 h-3" /> Increase letter (PDF)
                </button>
              )}
            </div>
          ))}
          {!profile?.timeline?.length && <div className="text-xs text-slate-400 py-4 text-center">No remuneration records yet.</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NewReviewDialog({ open, onOpenChange, registerRows, onCreated }) {
  const [form, setForm] = useState({ emp_ref: "", basis: "hourly", effective_date: "", new_base: "", annual_salary: "", std_weekly_hours: "38", review_type: "Annual review", reason: "", status: "Draft" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.emp_ref || !form.effective_date) { toast.error("Employee and effective date are required."); return; }
    setSaving(true);
    try {
      await api.post("/remuneration/reviews", {
        emp_ref: form.emp_ref, basis: form.basis, effective_date: form.effective_date,
        new_base: form.new_base ? Number(form.new_base) : null,
        annual_salary: form.annual_salary ? Number(form.annual_salary) : null,
        std_weekly_hours: Number(form.std_weekly_hours) || 38,
        review_type: form.review_type, reason: form.reason || null, status: form.status,
      });
      toast.success("Review recorded");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rem-new-review-dialog">
        <DialogHeader>
          <DialogTitle>New Remuneration Review</DialogTitle>
          <DialogDescription>Creates a dated transaction — history is never overwritten.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <div>
            <Label className="text-xs">Employee</Label>
            <Select value={form.emp_ref} onValueChange={(v) => set("emp_ref", v)}>
              <SelectTrigger data-testid="rem-review-employee-select" className="h-8 text-xs"><SelectValue placeholder="Select employee…" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {registerRows.map((r) => <SelectItem key={r.emp_ref} value={r.emp_ref} className="text-xs">{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Basis</Label>
              <Select value={form.basis} onValueChange={(v) => set("basis", v)}>
                <SelectTrigger data-testid="rem-review-basis-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{BASES.map((b) => <SelectItem key={b} value={b} className="text-xs">{b.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Effective date</Label>
              <Input data-testid="rem-review-date-input" type="date" className="h-8 text-xs" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {form.basis === "annual_salary" ? (
              <div>
                <Label className="text-xs">Annual salary ($)</Label>
                <Input data-testid="rem-review-salary-input" type="number" className="h-8 text-xs" value={form.annual_salary} onChange={(e) => set("annual_salary", e.target.value)} />
              </div>
            ) : (
              <div>
                <Label className="text-xs">Hourly rate ($)</Label>
                <Input data-testid="rem-review-rate-input" type="number" className="h-8 text-xs" value={form.new_base} onChange={(e) => set("new_base", e.target.value)} />
              </div>
            )}
            <div>
              <Label className="text-xs">Std weekly hours</Label>
              <Input type="number" className="h-8 text-xs" value={form.std_weekly_hours} onChange={(e) => set("std_weekly_hours", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="rem-review-status-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Draft", "Submitted", "Under review", "Approved"].map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason / notes</Label>
            <Textarea data-testid="rem-review-reason-input" className="text-xs" rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="rem-review-submit-btn" size="sm" onClick={submit} disabled={saving}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Save review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportPanel({ rows, reload }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [matchRow, setMatchRow] = useState(null);
  const [removeMissing, setRemoveMissing] = useState(false);
  const [bulkDate, setBulkDate] = useState("");
  const datesRef = useRef(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.get("/remuneration/match-candidates").then(({ data }) => setEmployees(data.rows || [])).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/remuneration/import/stage", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      toast.success(`Staged ${data.staged} rows (${data.ready} ready, ${data.exceptions} exceptions, ${data.excluded_chargeout} charge-out rows excluded)`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/remuneration/import/commit?remove_missing=${removeMissing}`, null, { timeout: 120000 });
      toast.success(`Committed ${data.imported} remuneration records${data.removed_missing ? `; ${data.removed_missing} employees removed (not on import)` : ""}`);
      if (data.skipped_missing_fields > 0) {
        toast.warning(`${data.skipped_missing_fields} row(s) NOT committed: ${(data.skipped_names || []).join("; ")}`, { duration: 12000 });
      }
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const patch = async (staging_id, body) => {
    try {
      await api.patch(`/remuneration/import/staging/${staging_id}`, body);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const shown = rows.filter((r) => filter === "all" ? true
    : filter === "exceptions" ? !!r.exception
    : filter === "ready" ? (!r.exception && r.kind !== "note" && r.status === "staged")
    : r.kind === filter);
  const readyCount = rows.filter((r) => !r.exception && r.kind !== "note" && r.status === "staged" && r.emp_ref && r.value != null && r.effective_date).length;

  const KIND_TONE = { current_rate: "bg-emerald-100 text-emerald-700", history: "bg-blue-100 text-blue-700", proposal: "bg-violet-100 text-violet-700", note: "bg-slate-100 text-slate-500" };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <FileSpreadsheet className="w-4 h-4 text-slate-400" />
        <div className="text-xs text-slate-600 flex-1 min-w-[200px]">
          Upload the wage review workbook (.xlsx). Rows are <b>staged only</b> — nothing hits the register until you commit. Charge-out rates are excluded automatically.
        </div>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="rem-import-file-input" />
        <Button data-testid="rem-import-upload-btn" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Upload workbook
        </Button>
        {rows.length > 0 && (
          <Button data-testid="rem-import-commit-btn" size="sm" disabled={busy || !readyCount} onClick={commit}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Commit {readyCount} rows
          </Button>
        )}
        {rows.length > 0 && (
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="rem-remove-missing-label">
            <input type="checkbox" data-testid="rem-remove-missing-checkbox" checked={removeMissing} onChange={(e) => setRemoveMissing(e.target.checked)} />
            Remove employees not on this import from the register
          </label>
        )}
      </div>

      {rows.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2" data-testid="rem-dates-toolbar">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mr-1">Effective dates</span>
          <Input data-testid="rem-bulk-date-input" type="date" className="h-7 text-xs w-36" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
          <Button data-testid="rem-bulk-date-btn" size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !bulkDate}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await api.post("/remuneration/import/staging/bulk-date", { effective_date: bulkDate, only_missing: true });
                toast.success(`Date applied to ${data.updated} rows that were missing one`);
                reload();
              } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
              finally { setBusy(false); }
            }}>
            Apply to all missing
          </Button>
          <span className="text-[10px] text-slate-400">or</span>
          <input ref={datesRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="rem-dates-file-input"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setBusy(true);
              try {
                const form = new FormData();
                form.append("file", f);
                const { data } = await api.post("/remuneration/import/staging/dates-upload", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
                toast.success(`${data.rows_updated} staging rows dated from spreadsheet`);
                if (data.unmatched_count > 0) toast.warning(`${data.unmatched_count} name(s) not found in staging: ${data.unmatched_names.slice(0, 5).join(", ")}${data.unmatched_count > 5 ? "…" : ""}`, { duration: 10000 });
                reload();
              } catch (e2) { toast.error(formatApiErrorDetail(e2?.response?.data?.detail)); }
              finally { setBusy(false); if (datesRef.current) datesRef.current.value = ""; }
            }} />
          <Button data-testid="rem-dates-upload-btn" size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => datesRef.current?.click()}>
            <Upload className="w-3 h-3 mr-1" /> Upload dates spreadsheet
          </Button>
          <span className="text-[10px] text-slate-400">(columns: employee name + effective date)</span>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="flex gap-1 flex-wrap">
            {["all", "ready", "exceptions", "current_rate", "history", "proposal", "note"].map((f) => (
              <button key={f} data-testid={`rem-import-filter-${f}`} onClick={() => setFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[10px] border ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
                {f.replace("_", " ")} ({f === "all" ? rows.length : f === "ready" ? rows.filter((r) => !r.exception && r.kind !== "note" && r.status === "staged").length : f === "exceptions" ? rows.filter((r) => r.exception).length : rows.filter((r) => r.kind === f).length})
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="rem-staging-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr><th className="text-left px-3 py-2">Kind</th><th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Matched</th><th className="text-left px-3 py-2">BU</th>
                  <th className="text-right px-3 py-2">Value</th><th className="text-left px-3 py-2">Basis</th>
                  <th className="text-left px-3 py-2">Effective</th><th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.staging_id} className={`border-t border-slate-100 ${r.exception ? "bg-amber-50/40" : ""}`}>
                    <td className="px-3 py-1.5"><Badge className={`${KIND_TONE[r.kind]} hover:bg-inherit text-[10px]`}>{r.kind?.replace("_", " ")}</Badge></td>
                    <td className="px-3 py-1.5 font-medium text-slate-800">{r.name}{r.year ? <span className="text-slate-400 font-normal"> · {r.year}</span> : null}</td>
                    <td className="px-3 py-1.5">
                      {r.matched_name ? r.matched_name : matchRow === r.staging_id ? (
                        <Select onValueChange={(v) => { setMatchRow(null); patch(r.staging_id, { emp_ref: v }); }}>
                          <SelectTrigger data-testid="rem-staging-match-select" className="h-6 text-[10px] w-40"><SelectValue placeholder="Match employee…" /></SelectTrigger>
                          <SelectContent className="max-h-56">
                            {employees.map((e) => (
                              <SelectItem key={e.emp_ref} value={e.emp_ref} className="text-xs">
                                {e.name}{e.excluded ? " (removed — will restore)" : ""}{e.gtc ? " (GTC)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <button className="text-amber-600 underline decoration-dotted" data-testid={`rem-staging-match-btn-${r.staging_id}`}
                          onClick={() => r.status === "staged" && setMatchRow(r.staging_id)}>
                          {r.exception || "match…"}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{r.bu || "—"}</td>
                    <td className="px-3 py-1.5 text-right">{r.value != null ? (r.basis === "annual_salary" ? fmtCurrency(r.value) : `$${r.value}`) : <span className="text-slate-400" title={r.raw_value}>{(r.raw_value || "—").slice(0, 14)}</span>}</td>
                    <td className="px-3 py-1.5">{r.basis?.replace("_", " ")}</td>
                    <td className="px-3 py-1.5">
                      {editRow === r.staging_id ? (
                        <Input type="date" autoFocus className="h-6 text-[10px] w-32" defaultValue={r.effective_date || ""}
                          data-testid="rem-staging-date-input"
                          onBlur={(e) => { setEditRow(null); if (e.target.value && e.target.value !== r.effective_date) patch(r.staging_id, { effective_date: e.target.value }); }} />
                      ) : (
                        <button className="underline decoration-dotted text-slate-700" data-testid={`rem-staging-date-${r.staging_id}`}
                          onClick={() => r.status === "staged" && setEditRow(r.staging_id)}>
                          {r.effective_date || "set date"}
                          {r.effective_date_confidence === "Year only" && <span className="text-amber-500 ml-1" title="Year only — click to confirm exact date">≈</span>}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <Badge className={`${r.status === "committed" ? "bg-emerald-100 text-emerald-700" : r.status === "excluded" ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-700"} hover:bg-inherit text-[10px]`}>{r.status}</Badge>
                      {r.status === "staged" && r.kind !== "note" && !r.exception && (!r.effective_date || r.value == null || !r.emp_ref) && (
                        <Badge className="ml-1 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]" data-testid={`rem-staging-blocked-${r.staging_id}`}>
                          {!r.effective_date ? "needs date" : r.value == null ? "needs value" : "needs match"}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.status === "staged" && r.kind !== "note" && (
                        <button className="text-[10px] text-slate-400 hover:text-rose-500" data-testid={`rem-staging-exclude-${r.staging_id}`}
                          onClick={() => patch(r.staging_id, { excluded: true })}>exclude</button>
                      )}
                      {r.status === "excluded" && (
                        <button className="text-[10px] text-slate-400 hover:text-emerald-600" onClick={() => patch(r.staging_id, { excluded: false })}>restore</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!shown.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">No rows in this filter</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
